import { config } from "@/shared/lib/config";
import type { BrandPrintLanguage } from "@/shared/lib/brand-document-print-language";
import { withBrandPrintLanguage } from "@/shared/lib/brand-document-print-language";
import { fetcher } from "@/shared/lib/fetcher";

export type AttendanceReportSummaryDTO = {
  totalAttendance: number;
  uniqueStudents: number;
};

export type AttendanceReportSummaryQuery = {
  dateFrom: string;
  dateTo: string;
  classTypeId?: string;
  levelId?: string;
  instructorId?: string;
};

export type AttendanceReportTrendItemDTO = {
  date: string;
  totalAttendance: number;
  uniqueStudents: number;
};

export type AttendanceReportTrendQuery = {
  dateFrom: string;
  dateTo: string;
  groupBy?: "day" | "week";
  classTypeId?: string;
  levelId?: string;
  instructorId?: string;
};

export type RoomUsageSummaryDTO = {
  totalHoursUsed: number;
  sessionsCount: number;
  peakTimeStart: string | null;
  peakTimeEnd: string | null;
};

export type RoomUsageSummaryQuery = {
  dateFrom: string;
  dateTo: string;
  roomId?: string;
};

export type RoomUsageByRoomItemDTO = {
  roomId: string;
  roomName: string;
  hoursUsed: number;
  sessionsCount: number;
};

export type RoomUsageTrendItemDTO = {
  date: string;
  totalHoursUsed: number;
  sessionsCount: number;
};

export type RoomUsagePeakItemDTO = {
  hourSlot: string;
  count: number;
};

export type InstructorSessionSummaryDTO = {
  sessionsTaught: number;
  estimatedPayout: number;
};

export type InstructorSessionSummaryQuery = {
  dateFrom: string;
  dateTo: string;
  instructorId?: string;
};

export type InstructorSessionByInstructorItemDTO = {
  instructorId: string;
  instructorName: string;
  bankAccountNumber?: string | null;
  sessionsTaught: number;
  generalClassSessions: number;
  privateLessonSessions: number;
  studioClassSessions: number;
  offSiteClassSessions: number;
  studioPrivateLessonSessions: number;
  offSitePrivateLessonSessions: number;
  offSiteNearPrivateLessonSessions: number;
  offSiteFarPrivateLessonSessions: number;
  partnerInstructorOnlyClassSessions: number;
  roomRentalAssistSessions: number;
  generalClassAssistSessions?: number;
  generalClassAssistStudioSessions?: number;
  generalClassAssistOffSiteSessions?: number;
  billableHoursTotal: number;
  billableHoursGeneralClass: number;
  billableHoursGeneralClassStudio: number;
  billableHoursGeneralClassOffSite: number;
  billableHoursPrivateLesson: number;
  billableHoursPrivateLessonStudio: number;
  billableHoursPrivateLessonOffSite: number;
  billableHoursPrivateLessonOffSiteNear: number;
  billableHoursPrivateLessonOffSiteFar: number;
  billableHoursPartner: number;
  generalClassRatePerHour: number;
  generalClassOffSiteRatePerHour: number;
  privateLessonRatePerHour: number;
  privateLessonOffSiteRatePerHour: number;
  privateLessonOffSiteFarRatePerHour: number;
  partnerRatePerHour: number;
  roomRentalAssistRatePerSession: number;
  generalClassAssistStudioRatePerSession?: number;
  generalClassAssistOffSiteRatePerSession?: number;
  generalClassPayoutBasis: "per_hour" | "per_session";
  generalClassOffSitePayoutBasis: "per_hour" | "per_session";
  privateLessonPayoutBasis: "per_hour" | "per_session";
  privateLessonOffSitePayoutBasis: "per_hour" | "per_session";
  privateLessonOffSiteFarPayoutBasis: "per_hour" | "per_session";
  partnerPayoutBasis: "per_hour" | "per_session";
  roomRentalAssistPayoutBasis: "per_hour" | "per_session";
  generalClassAssistStudioPayoutBasis?: "per_hour" | "per_session";
  generalClassAssistOffSitePayoutBasis?: "per_hour" | "per_session";
  estimatedPayoutGeneralClass: number;
  estimatedPayoutGeneralClassStudio: number;
  estimatedPayoutGeneralClassOffSite: number;
  estimatedPayoutPrivateLesson: number;
  estimatedPayoutPrivateLessonStudio: number;
  estimatedPayoutPrivateLessonOffSite: number;
  estimatedPayoutPrivateLessonOffSiteNear: number;
  estimatedPayoutPrivateLessonOffSiteFar: number;
  estimatedPayoutPartner: number;
  estimatedPayoutRoomRentalAssist: number;
  estimatedPayoutGeneralClassAssist?: number;
  estimatedPayoutGeneralClassAssistStudio?: number;
  estimatedPayoutGeneralClassAssistOffSite?: number;
  estimatedPayout: number;
  monthlyFreeGeneralClassSessions?: number;
  retainerFreeGeneralClassSessions?: number;
  billableGeneralClassLeadSessions?: number;
};

export type InstructorMonthlyPayoutQuery = {
  year: number;
  month: number;
  instructorId?: string;
};

export type InstructorMonthlyPayoutLinesQuery = {
  year: number;
  month: number;
  instructorId: string;
};

export type InstructorMonthlyPayoutLineDTO = {
  date: string;
  sourceType: "class_session" | "private_booking" | "room_booking";
  sourceId: string;
  sessionLocationKind: "studio" | "off_site";
  offSiteDistance?: "near" | "far" | null;
  engagementKind: "standard" | "partner_instructor_only";
  payoutWorkKind:
    | "general_class"
    | "private_lesson"
    | "partner"
    | "room_rental_assist"
    | "general_class_assist";
  payoutLocationKind: "studio" | "off_site" | "off_site_near" | "off_site_far";
  payoutBasis: "per_hour" | "per_session";
  hours: number;
  amountKip: number;
  linePayoutKip: number;
  grossLinePayoutKip: number;
  calculatedLinePayoutKip: number;
  isAmountOverridden: boolean;
  overrideNote: string | null;
  monthGeneralClassLeadIndex: number | null;
  isRetainerFreeSession: boolean;
  startTime?: string | null;
  endTime?: string | null;
  roomName?: string | null;
  bookingLabel?: string | null;
  className?: string | null;
  studentName?: string | null;
};

export type InstructorPayrollBatchSummaryDTO = {
  id: string;
  year: number;
  month: number;
  dateFrom: string;
  dateTo: string;
  status: "confirmed" | "paid";
  totalAmountKip: number;
  instructorCount: number;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  confirmedAt: string;
  paidAt: string | null;
};

export type InstructorPayrollLineDTO = {
  id: string;
  batchId: string;
  instructorId: string;
  amountKip: number;
  summarySnapshot: InstructorSessionByInstructorItemDTO;
  createdAt: string;
};

export type InstructorPayrollBatchDetailDTO =
  InstructorPayrollBatchSummaryDTO & {
    lines: InstructorPayrollLineDTO[];
  };

export type RevenueSummaryDTO = {
  totalRevenue: number;
  packageRevenue: number;
  privateLessonRevenue: number;
  roomBookingRevenue: number;
};

export type RevenueSummaryQuery = {
  dateFrom: string;
  dateTo: string;
  issuedById?: string;
};

export type RevenueTrendItemDTO = {
  period: string;
  totalRevenue: number;
  packageRevenue: number;
  privateLessonRevenue: number;
  roomBookingRevenue: number;
};

export type RevenueTrendQuery = {
  dateFrom: string;
  dateTo: string;
  groupBy?: "day" | "month";
  issuedById?: string;
};

export type PackageSalesByTemplateItemDTO = {
  templateId: string;
  templateName: string;
  packageType: "package" | "private_lesson";
  salesCount: number;
  revenue: number;
  classTypes?: string[];
  levels?: string[];
};

export type PackageSalesByTemplateQuery = {
  dateFrom: string;
  dateTo: string;
  packageType?: "package" | "private_lesson" | "all";
  classTypeId?: string;
  levelId?: string;
  sortBy?: "count" | "revenue";
  limit?: number;
};

export type PackageSalesSummaryDTO = {
  totalSalesCount: number;
  totalRevenue: number;
};

export type PackageSalesSummaryQuery = {
  dateFrom: string;
  dateTo: string;
  packageType?: "package" | "private_lesson" | "all";
  classTypeId?: string;
  levelId?: string;
};

export type PackageSalesByClassTypeItemDTO = {
  classTypeId: string;
  classTypeName: string;
  salesCount: number;
  revenue: number;
};

export type PackageSalesByClassTypeQuery = {
  dateFrom: string;
  dateTo: string;
  levelId?: string;
  sortBy?: "count" | "revenue";
};

export type RefundReportSummaryDTO = {
  totalCount: number;
  requestedCount: number;
  approvedCount: number;
  rejectedCount: number;
  refundedCount: number;
  totalRefundAmountRequested: number;
  totalRefundAmountCompleted: number;
};

export type RefundReportReasonsItemDTO = {
  reason: string;
  count: number;
};

export type RefundReportQuery = {
  filters?: Array<{ field: string; op: string; value: unknown }>;
};

export type StudentReportSummaryDTO = {
  newStudentsCount: number;
  activeStudentsCount: number;
  inactiveStudentsCount: number;
  totalStudents: number;
};

export type StudentReportSummaryQuery = {
  dateFrom: string;
  dateTo: string;
  groupId?: string;
};

export type StudentReportUtilizationDTO = {
  avgRemainingSessions: number | null;
  avgRemainingHours: number | null;
  totalRemainingSessions: number;
  totalRemainingHours: number;
  packageCount: number;
  privateLessonPackageCount: number;
};

export type StudentReportUtilizationQuery = {
  groupId?: string;
  packageType?: "package" | "private_lesson" | "all";
};

export type StudentReportNewTrendItemDTO = {
  period: string;
  count: number;
};

export type StudentReportNewTrendQuery = {
  dateFrom: string;
  dateTo: string;
  groupBy?: "day" | "month";
  groupId?: string;
};

export type StudentReportLowRemainingItemDTO = {
  studentId: string;
  studentName: string;
  memberId: string;
  packageType: "package" | "private_lesson";
  packageName: string;
  remainingSessions?: number;
  totalSessions?: number;
  remainingHours?: number;
  totalHours?: number;
};

export type StudentReportLowRemainingQuery = {
  groupId?: string;
  packageType?: "package" | "private_lesson" | "all";
  thresholdSessions?: number;
  thresholdHours?: number;
  limit?: number;
};

export type StudentPackagesReportItemDTO = {
  studentId: string;
  studentName: string;
  memberId: string;
  packageId: string;
  packageType: "package" | "private_lesson";
  packageName: string;
  studyDescription: string;
  paidAmount: number;
  unpaidAmount: number;
  usedUnits: number;
  remainingUnits: number;
  unitType: "session" | "hour";
  expireDate: string;
  status: string;
  purchasedAt: string;
  paidAt: string | null;
};

export type StudentPackagesReportQuery = {
  dateFrom?: string;
  dateTo?: string;
  groupId?: string;
  packageType?: "package" | "private_lesson" | "all";
  limit?: number;
};

export type ActiveStudentsByClassItemDTO = {
  classTypeId: string;
  classTypeName: string;
  levelId: string;
  levelName: string;
  levelOrder: number;
  activeStudentCount: number;
};

export type ActiveStudentsByClassDTO = {
  asOfDate: string;
  items: ActiveStudentsByClassItemDTO[];
  totalDistinctStudents: number;
};

export type ActiveStudentsByClassQuery = {
  classTypeId?: string;
  levelId?: string;
  groupId?: string;
  includeEmpty?: boolean;
  sortBy?: "count" | "className";
};

export type ClassCapacitySummaryDTO = {
  avgUtilizationPercent: number;
  fullClassesCount: number;
  emptyClassesCount: number;
  totalSessionsCount: number;
  peakTimeSlot: string | null;
};

export type ClassCapacitySummaryQuery = {
  dateFrom: string;
  dateTo: string;
  classTypeId?: string;
  levelId?: string;
  roomId?: string;
  locationKind?: "studio" | "off_site";
  engagementKind?: "standard" | "partner_instructor_only";
};

export type ClassCapacitySessionItemDTO = {
  sessionId: string;
  date: string;
  startTime: string;
  classTypeName: string;
  levelName: string;
  locationKind: "studio" | "off_site";
  offSiteLabel: string | null;
  engagementKind: "standard" | "partner_instructor_only";
  roomName: string;
  attendance: number;
  capacity: number;
  utilizationPercent: number;
  status: "full" | "empty" | "normal";
};

export type ClassCapacitySessionsQuery = {
  dateFrom: string;
  dateTo: string;
  classTypeId?: string;
  levelId?: string;
  roomId?: string;
  locationKind?: "studio" | "off_site";
  engagementKind?: "standard" | "partner_instructor_only";
  sortBy?: "date" | "utilization" | "attendance";
  sortDir?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type ClassCapacitySessionsPageDTO = {
  data: ClassCapacitySessionItemDTO[];
  meta: { total: number; limit: number; offset: number };
};

export type ClassCapacityPeakTimeItemDTO = {
  hourSlot: string;
  sessionCount: number;
  avgUtilization: number;
  totalAttendance: number;
};

export const reportsApi = {
  attendance: {
    async getSummary(
      query: AttendanceReportSummaryQuery,
    ): Promise<AttendanceReportSummaryDTO> {
      const url = new URL(`${config.apiUrl}/reports/attendance/summary`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.classTypeId)
        url.searchParams.set("classTypeId", query.classTypeId);
      if (query.levelId) url.searchParams.set("levelId", query.levelId);
      if (query.instructorId)
        url.searchParams.set("instructorId", query.instructorId);
      return fetcher.get<AttendanceReportSummaryDTO>(url.toString());
    },
    async getTrend(
      query: AttendanceReportTrendQuery,
    ): Promise<AttendanceReportTrendItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/attendance/trend`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.groupBy) url.searchParams.set("groupBy", query.groupBy);
      if (query.classTypeId)
        url.searchParams.set("classTypeId", query.classTypeId);
      if (query.levelId) url.searchParams.set("levelId", query.levelId);
      if (query.instructorId)
        url.searchParams.set("instructorId", query.instructorId);
      return fetcher.get<AttendanceReportTrendItemDTO[]>(url.toString());
    },
  },
  classCapacity: {
    async getSummary(
      query: ClassCapacitySummaryQuery,
    ): Promise<ClassCapacitySummaryDTO> {
      const url = new URL(`${config.apiUrl}/reports/class-capacity/summary`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.classTypeId)
        url.searchParams.set("classTypeId", query.classTypeId);
      if (query.levelId) url.searchParams.set("levelId", query.levelId);
      if (query.roomId) url.searchParams.set("roomId", query.roomId);
      if (query.locationKind)
        url.searchParams.set("locationKind", query.locationKind);
      if (query.engagementKind)
        url.searchParams.set("engagementKind", query.engagementKind);
      return fetcher.get<ClassCapacitySummaryDTO>(url.toString());
    },
    async getSessions(
      query: ClassCapacitySessionsQuery,
    ): Promise<ClassCapacitySessionsPageDTO> {
      const url = new URL(`${config.apiUrl}/reports/class-capacity/sessions`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.classTypeId)
        url.searchParams.set("classTypeId", query.classTypeId);
      if (query.levelId) url.searchParams.set("levelId", query.levelId);
      if (query.roomId) url.searchParams.set("roomId", query.roomId);
      if (query.locationKind)
        url.searchParams.set("locationKind", query.locationKind);
      if (query.engagementKind)
        url.searchParams.set("engagementKind", query.engagementKind);
      if (query.sortBy) url.searchParams.set("sortBy", query.sortBy);
      if (query.sortDir) url.searchParams.set("sortDir", query.sortDir);
      if (query.limit != null)
        url.searchParams.set("limit", String(query.limit));
      if (query.offset != null)
        url.searchParams.set("offset", String(query.offset));
      return fetcher.get<ClassCapacitySessionsPageDTO>(url.toString());
    },
    async getPeakTimes(
      query: ClassCapacitySummaryQuery,
    ): Promise<ClassCapacityPeakTimeItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/class-capacity/peak-times`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.classTypeId)
        url.searchParams.set("classTypeId", query.classTypeId);
      if (query.levelId) url.searchParams.set("levelId", query.levelId);
      if (query.roomId) url.searchParams.set("roomId", query.roomId);
      if (query.locationKind)
        url.searchParams.set("locationKind", query.locationKind);
      if (query.engagementKind)
        url.searchParams.set("engagementKind", query.engagementKind);
      return fetcher.get<ClassCapacityPeakTimeItemDTO[]>(url.toString());
    },
  },
  roomUsage: {
    async getSummary(
      query: RoomUsageSummaryQuery,
    ): Promise<RoomUsageSummaryDTO> {
      const url = new URL(`${config.apiUrl}/reports/room-usage/summary`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.roomId) url.searchParams.set("roomId", query.roomId);
      return fetcher.get<RoomUsageSummaryDTO>(url.toString());
    },
    async getByRoom(
      query: RoomUsageSummaryQuery,
    ): Promise<RoomUsageByRoomItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/room-usage/by-room`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.roomId) url.searchParams.set("roomId", query.roomId);
      return fetcher.get<RoomUsageByRoomItemDTO[]>(url.toString());
    },
    async getTrend(
      query: RoomUsageSummaryQuery,
    ): Promise<RoomUsageTrendItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/room-usage/trend`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.roomId) url.searchParams.set("roomId", query.roomId);
      return fetcher.get<RoomUsageTrendItemDTO[]>(url.toString());
    },
    async getPeak(
      query: RoomUsageSummaryQuery,
    ): Promise<RoomUsagePeakItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/room-usage/peak`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.roomId) url.searchParams.set("roomId", query.roomId);
      return fetcher.get<RoomUsagePeakItemDTO[]>(url.toString());
    },
  },
  instructorSession: {
    async getSummary(
      query: InstructorSessionSummaryQuery,
    ): Promise<InstructorSessionSummaryDTO> {
      const url = new URL(
        `${config.apiUrl}/reports/instructor-session/summary`,
      );
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.instructorId)
        url.searchParams.set("instructorId", query.instructorId);
      return fetcher.get<InstructorSessionSummaryDTO>(url.toString());
    },
    async getByInstructor(
      query: InstructorSessionSummaryQuery,
    ): Promise<InstructorSessionByInstructorItemDTO[]> {
      const url = new URL(
        `${config.apiUrl}/reports/instructor-session/by-instructor`,
      );
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.instructorId)
        url.searchParams.set("instructorId", query.instructorId);
      return fetcher.get<InstructorSessionByInstructorItemDTO[]>(
        url.toString(),
      );
    },
  },
  instructorMonthlyPayout: {
    async getSummary(
      query: InstructorMonthlyPayoutQuery,
    ): Promise<InstructorSessionByInstructorItemDTO[]> {
      const url = new URL(
        `${config.apiUrl}/reports/instructor-monthly-payout/summary`,
      );
      url.searchParams.set("year", String(query.year));
      url.searchParams.set("month", String(query.month));
      if (query.instructorId)
        url.searchParams.set("instructorId", query.instructorId);
      return fetcher.get<InstructorSessionByInstructorItemDTO[]>(
        url.toString(),
      );
    },
    async getLines(
      query: InstructorMonthlyPayoutLinesQuery,
    ): Promise<InstructorMonthlyPayoutLineDTO[]> {
      const url = new URL(
        `${config.apiUrl}/reports/instructor-monthly-payout/lines`,
      );
      url.searchParams.set("year", String(query.year));
      url.searchParams.set("month", String(query.month));
      url.searchParams.set("instructorId", query.instructorId);
      return fetcher.get<InstructorMonthlyPayoutLineDTO[]>(url.toString());
    },
    async upsertLineOverride(body: {
      year: number;
      month: number;
      instructorId: string;
      sourceType: InstructorMonthlyPayoutLineDTO["sourceType"];
      sourceId: string;
      amountKip: number;
      note?: string | null;
    }) {
      return fetcher.put<{
        id: string;
        amountKip: number;
        note: string | null;
      }>(`${config.apiUrl}/reports/instructor-monthly-payout/lines/override`, body);
    },
    async deleteLineOverride(query: {
      year: number;
      month: number;
      instructorId: string;
      sourceType: InstructorMonthlyPayoutLineDTO["sourceType"];
      sourceId: string;
    }) {
      const url = new URL(
        `${config.apiUrl}/reports/instructor-monthly-payout/lines/override`,
      );
      url.searchParams.set("year", String(query.year));
      url.searchParams.set("month", String(query.month));
      url.searchParams.set("instructorId", query.instructorId);
      url.searchParams.set("sourceType", query.sourceType);
      url.searchParams.set("sourceId", query.sourceId);
      return fetcher.delete<{ ok: true }>(url.toString());
    },
    async getPayrollBatchByMonth(
      year: number,
      month: number,
    ): Promise<InstructorPayrollBatchSummaryDTO | null> {
      const url = new URL(
        `${config.apiUrl}/reports/instructor-monthly-payout/payroll-batch/month`,
      );
      url.searchParams.set("year", String(year));
      url.searchParams.set("month", String(month));
      return fetcher.get<InstructorPayrollBatchSummaryDTO | null>(
        url.toString(),
      );
    },
    async getPayrollBatchById(
      batchId: string,
    ): Promise<InstructorPayrollBatchDetailDTO | null> {
      return fetcher.get<InstructorPayrollBatchDetailDTO | null>(
        `${config.apiUrl}/reports/instructor-monthly-payout/payroll-batches/${batchId}`,
      );
    },
    async createPayrollBatch(body: {
      year: number;
      month: number;
      instructorIds: string[];
      notes?: string | null;
    }): Promise<InstructorPayrollBatchSummaryDTO> {
      return fetcher.post<InstructorPayrollBatchSummaryDTO>(
        `${config.apiUrl}/reports/instructor-monthly-payout/payroll-batches`,
        body,
      );
    },
    async markPayrollBatchPaid(
      batchId: string,
    ): Promise<InstructorPayrollBatchSummaryDTO> {
      return fetcher.patch<InstructorPayrollBatchSummaryDTO>(
        `${config.apiUrl}/reports/instructor-monthly-payout/payroll-batches/${batchId}/mark-paid`,
        {},
      );
    },
    printUrl(
      query: InstructorMonthlyPayoutQuery,
      lang: BrandPrintLanguage = "lo",
    ): string {
      const url = new URL(
        `${config.apiUrl}/reports/instructor-monthly-payout/print`,
      );
      url.searchParams.set("year", String(query.year));
      url.searchParams.set("month", String(query.month));
      if (query.instructorId) {
        url.searchParams.set("instructorId", query.instructorId);
      }
      return withBrandPrintLanguage(url.toString(), lang);
    },
  },
  revenue: {
    async getSummary(query: RevenueSummaryQuery): Promise<RevenueSummaryDTO> {
      const url = new URL(`${config.apiUrl}/reports/revenue/summary`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.issuedById)
        url.searchParams.set("issuedById", query.issuedById);
      return fetcher.get<RevenueSummaryDTO>(url.toString());
    },
    async getTrend(query: RevenueTrendQuery): Promise<RevenueTrendItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/revenue/trend`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.groupBy) url.searchParams.set("groupBy", query.groupBy);
      if (query.issuedById)
        url.searchParams.set("issuedById", query.issuedById);
      return fetcher.get<RevenueTrendItemDTO[]>(url.toString());
    },
  },
  packageSales: {
    async getByTemplate(
      query: PackageSalesByTemplateQuery,
    ): Promise<PackageSalesByTemplateItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/package-sales/by-template`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      url.searchParams.set("packageType", query.packageType ?? "all");
      if (query.classTypeId)
        url.searchParams.set("classTypeId", query.classTypeId);
      if (query.levelId) url.searchParams.set("levelId", query.levelId);
      if (query.sortBy) url.searchParams.set("sortBy", query.sortBy);
      if (query.limit != null)
        url.searchParams.set("limit", String(query.limit));
      return fetcher.get<PackageSalesByTemplateItemDTO[]>(url.toString());
    },
    async getSummary(
      query: PackageSalesSummaryQuery,
    ): Promise<PackageSalesSummaryDTO> {
      const url = new URL(`${config.apiUrl}/reports/package-sales/summary`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.packageType)
        url.searchParams.set("packageType", query.packageType);
      if (query.classTypeId)
        url.searchParams.set("classTypeId", query.classTypeId);
      if (query.levelId) url.searchParams.set("levelId", query.levelId);
      return fetcher.get<PackageSalesSummaryDTO>(url.toString());
    },
    async getByClassType(
      query: PackageSalesByClassTypeQuery,
    ): Promise<PackageSalesByClassTypeItemDTO[]> {
      const url = new URL(
        `${config.apiUrl}/reports/package-sales/by-class-type`,
      );
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.levelId) url.searchParams.set("levelId", query.levelId);
      if (query.sortBy) url.searchParams.set("sortBy", query.sortBy);
      return fetcher.get<PackageSalesByClassTypeItemDTO[]>(url.toString());
    },
  },
  students: {
    async getSummary(
      query: StudentReportSummaryQuery,
    ): Promise<StudentReportSummaryDTO> {
      const url = new URL(`${config.apiUrl}/reports/students/summary`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.groupId) url.searchParams.set("groupId", query.groupId);
      return fetcher.get<StudentReportSummaryDTO>(url.toString());
    },
    async getUtilization(
      query: StudentReportUtilizationQuery,
    ): Promise<StudentReportUtilizationDTO> {
      const url = new URL(`${config.apiUrl}/reports/students/utilization`);
      if (query.groupId) url.searchParams.set("groupId", query.groupId);
      if (query.packageType)
        url.searchParams.set("packageType", query.packageType);
      return fetcher.get<StudentReportUtilizationDTO>(url.toString());
    },
    async getNewTrend(
      query: StudentReportNewTrendQuery,
    ): Promise<StudentReportNewTrendItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/students/new-trend`);
      url.searchParams.set("dateFrom", query.dateFrom);
      url.searchParams.set("dateTo", query.dateTo);
      if (query.groupBy) url.searchParams.set("groupBy", query.groupBy);
      if (query.groupId) url.searchParams.set("groupId", query.groupId);
      return fetcher.get<StudentReportNewTrendItemDTO[]>(url.toString());
    },
    async getLowRemaining(
      query: StudentReportLowRemainingQuery,
    ): Promise<StudentReportLowRemainingItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/students/low-remaining`);
      if (query.groupId) url.searchParams.set("groupId", query.groupId);
      if (query.packageType)
        url.searchParams.set("packageType", query.packageType);
      if (query.thresholdSessions != null)
        url.searchParams.set(
          "thresholdSessions",
          String(query.thresholdSessions),
        );
      if (query.thresholdHours != null)
        url.searchParams.set("thresholdHours", String(query.thresholdHours));
      if (query.limit != null)
        url.searchParams.set("limit", String(query.limit));
      return fetcher.get<StudentReportLowRemainingItemDTO[]>(url.toString());
    },
    async getPackagesReport(
      query: StudentPackagesReportQuery,
    ): Promise<StudentPackagesReportItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/students/packages`);
      if (query.dateFrom) url.searchParams.set("dateFrom", query.dateFrom);
      if (query.dateTo) url.searchParams.set("dateTo", query.dateTo);
      if (query.groupId) url.searchParams.set("groupId", query.groupId);
      if (query.packageType)
        url.searchParams.set("packageType", query.packageType);
      if (query.limit != null)
        url.searchParams.set("limit", String(query.limit));
      return fetcher.get<StudentPackagesReportItemDTO[]>(url.toString());
    },
    async getActiveByClass(
      query: ActiveStudentsByClassQuery,
    ): Promise<ActiveStudentsByClassDTO> {
      const url = new URL(
        `${config.apiUrl}/reports/students/active-by-class`,
      );
      if (query.classTypeId)
        url.searchParams.set("classTypeId", query.classTypeId);
      if (query.levelId) url.searchParams.set("levelId", query.levelId);
      if (query.groupId) url.searchParams.set("groupId", query.groupId);
      if (query.includeEmpty === false)
        url.searchParams.set("includeEmpty", "false");
      if (query.sortBy) url.searchParams.set("sortBy", query.sortBy);
      return fetcher.get<ActiveStudentsByClassDTO>(url.toString());
    },
  },
  refund: {
    async getSummary(
      query: RefundReportQuery,
    ): Promise<RefundReportSummaryDTO> {
      const url = new URL(`${config.apiUrl}/reports/refund/summary`);
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<RefundReportSummaryDTO>(url.toString());
    },
    async getReasons(
      query: RefundReportQuery,
    ): Promise<RefundReportReasonsItemDTO[]> {
      const url = new URL(`${config.apiUrl}/reports/refund/reasons`);
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<RefundReportReasonsItemDTO[]>(url.toString());
    },
  },
};
